import {useEffect} from 'react';

import Grid from '@mui/material/Grid';
import * as Sentry from '@sentry/react';
import {func, oneOfType, node, bool} from 'prop-types';
import {useLocation, useNavigate} from 'react-router-dom';
import styled from 'styled-components';

import WidgetsContainer from 'components/widgets/WidgetsContainer';
import useProfile from 'hooks/providers/useProfile';

import TranscriptWorkingDayModal from './components/audio-transcript/TranscriptWorkingDayModal/TranscriptWorkingDayModal';
import ChatbotSidebar from './components/chatbot/ChatbotSidebar/ChatbotSidebar';
import ContactFormBackdrop from './components/contact/ContactFormBackdrop/ContactFormBackdrop';
import Header from './components/header/Header/Header';
import HeaderOnboardingPopper from './components/header/onboarding/HeaderOnboardingPopper/HeaderOnboardingPopper';
import ReportModulesSidebar from './components/main-sidebar/ReportModulesSidebar/ReportModulesSidebar';
import OnboardingWelcomeModal from './components/onboarding/welcome-modal/OnboardingWelcomeModal/OnboardingWelcomeModal';
import AppSnackbars from './components/snackbar/AppSnackbars';
import Spinner from './components/spinner/Spinner';
import UploadDialog from './components/upload/UploadDialog';
import {DATA_INTEGRATION_JOBS_ACTIVE_STATUSES} from './const';
import useRefetchAfterAdminModeToggling from './hooks/components/layout/useRefetchAfterAdminModeToggling';
import useAuth from './hooks/providers/useAuth';
import useLoading from './hooks/providers/useLoading';
import usePayment from './hooks/providers/usePayment';
import useProjects from './hooks/providers/useProjects';
import useReport from './hooks/providers/useReport';
import useReportParameters from './hooks/providers/useReportParameters';
import useReportViews from './hooks/providers/useReportViews';
import useSnackbar from './hooks/providers/useSnackbar';
import useUpload from './hooks/providers/useUpload';
import useWorkspaces from './hooks/providers/useWorkspaces';
import Report from './pages/Report/Report';
import {makeHumanFriendlyUrl} from './utils/data-formatting';

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: calc(100vh - 64px);
  width: 100%;
`;

const Layout = ({children}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {setSelectedReport} = useReport();
  const {isRegeneratingToken, user, isNewUser} = useAuth();
  const {profile, isConsentsLoading, getHelpButtons} = useProfile();
  const {integrationJobs, pollIntegrationJob} = useProjects();
  const {updateReportJobs, pollUpdateReportJob} = useReportParameters();
  const {isUploadFilesModalOpen} = useUpload();
  const {setCompletedApiCalls} = useLoading();
  const {getWorkspaces} = useWorkspaces();
  const {allReportsViews, getReportsViews} = useReportViews();
  const {setCart, subscription, products} = usePayment();

  useRefetchAfterAdminModeToggling();

  useEffect(() => {
    (async () => {
      if (isNewUser && user?.tokenAad) {
        await getWorkspaces();
        setCompletedApiCalls(prevCompleted => prevCompleted + 1);
      }
    })();
  }, [user?.tokenAad]);

  useEffect(() => {
    if (profile?.id) {
      Sentry.setUser({id: profile.id});
    }
  }, [profile?.id]);

  const isReportPage = location.pathname.startsWith('/reports');

  useEffect(() => {
    if (!isReportPage) {
      setSelectedReport(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    (async () => {
      if (allReportsViews.length === 0) {
        getReportsViews();
      }
    })();
  }, [user?.tokenAad]);

  // This hooks sets the initial cart when coming to products page
  useEffect(() => {
    if (subscription && products.length > 0) {
      const currentCart = (subscription.products || []).map(subscribedProduct => {
        return {
          id: subscribedProduct.product_id,
          quantity: (subscribedProduct.quantity || 0) < subscribedProduct.minimum_quantity ? subscribedProduct.minimum_quantity : subscribedProduct.quantity,
          minimum_quantity: subscribedProduct.minimum_quantity,
          ...products.find(p => p.id === subscribedProduct.product_id)
        };
      });
      setCart(currentCart);
    }
  }, [subscription, products, location.pathname]);

  const jobs = Object.values(integrationJobs).filter(job => !job.handled && DATA_INTEGRATION_JOBS_ACTIVE_STATUSES.includes(job.status));

  useEffect(() => {
    if (jobs.length === 0) return;

    const intervalId = setInterval(() => {
      jobs.forEach(pollIntegrationJob);
    }, 10000);

    return () => clearInterval(intervalId);
  }, [integrationJobs]);

  useEffect(() => {
    if (updateReportJobs.length === 0) return;
    const intervalId = setInterval(() => {
      Object.values(updateReportJobs)
        .filter(job => !job.handled) // TODO adapt with statuses as above if needed ?
        .forEach(pollUpdateReportJob);
    }, 3000);

    return () => clearInterval(intervalId);
  }, [updateReportJobs]);

  // This hooks aims to redirect user to the onboarding document
  // Navigates new users to //base-documentaire/livret-de-bienvenue
  useEffect(() => {
    if (getHelpButtons && profile?.newUser) {
      const categories = Object.keys(getHelpButtons);
      for (let i = 0; i < categories.length; i++) {
        const categoryKey = categories[i];
        const category = getHelpButtons[categoryKey];
        const welcomeDoc = category?.find(doc => doc.is_welcome_doc);
        if (welcomeDoc && welcomeDoc.subtitle && !localStorage.getItem('welcomedocopened')) {
          localStorage.setItem('welcomedocopened', true);
          const formattedGroupName = makeHumanFriendlyUrl(categoryKey);
          const formattedDocumentName = makeHumanFriendlyUrl(welcomeDoc.subtitle);
          navigate(`/${formattedGroupName}/${formattedDocumentName}`);
          break;
        }
      }
    }
  }, [getHelpButtons, navigate, profile]);

  // Forces report container to be on a single page without scroll
  const fullHeightContainerStyle = {
    height: '100vh',
    overflow: 'hidden'
  };

  // !isUploadFilesModalOpen because this style breaks react-dropzone.
  const containerStyle = location?.pathname?.startsWith('/reports') && !isUploadFilesModalOpen ? fullHeightContainerStyle : {};

  return (
    <div style={containerStyle}>
      <Header />
      <AppSnackbars />

      <Grid container flex={1}>
        <ReportModulesSidebar />

        <ChatbotSidebar>
          {isConsentsLoading || isRegeneratingToken ? (
            <SpinnerContainer>
              <Spinner withoutText size={128} isLoading />
            </SpinnerContainer>
          ) : (
            <>
              {children}
              <WidgetsContainer />
              <Report />
            </>
          )}
        </ChatbotSidebar>
      </Grid>

      <UploadDialog />
      <TranscriptWorkingDayModal />
      <ContactFormBackdrop />
      <OnboardingWelcomeModal />
      <HeaderOnboardingPopper />
    </div>
  );
};

Layout.propTypes = {
  children: oneOfType([node, func])
};

export default Layout;
